import { useState, useEffect } from 'preact/hooks';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import { navigate } from '../lib/navigate';
import { guestApi, publicApi, setCsrfToken } from '../api/client';
import type { InvitationPreview } from '../api/types';
import { useAuth } from '../hooks/useAuth';
import { DisplayNameForm } from '../components/DisplayNameForm';
import { Skeleton } from '../components/Skeleton';
import {
  ChorusHeader,
  ChorusEmpty,
  ChorusHero,
  inviteIllustrationLabels,
  AvatarStack,
  authorColor,
  IconArrow,
  IconPlus,
} from '../components/chorus';
import joinStyles from './JoinPage.module.css';
import styles from './CodeJoinPage.module.css';

const THREAD_TYPE_LABELS: Record<string, string> = {
  memory: 'Throwback',
  legacy: 'Origin Story',
  event: 'Event',
  topic: 'Topic',
  milestone: 'Milestone',
};

const THREAD_TYPE_DESCRIPTIONS: Record<string, string> = {
  memory: 'A shared memory told from multiple perspectives.',
  legacy: 'A life story told through many voices.',
  event: 'A shared experience captured from different angles.',
  topic: 'An open discussion from different viewpoints.',
  milestone: 'A milestone celebrated by the people who were there.',
};

interface CodeJoinPageProps {
  code?: string;
  path?: string;
}

export function CodeJoinPage({ code }: CodeJoinPageProps) {
  const [step, setStep] = useState<'loading' | 'name' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const { setGuestSession } = useAuth();

  useEffect(() => {
    if (!code) {
      setStep('error');
      setError('No invite code found. Check that the full code was entered.');
      return;
    }

    trackCaptureEvent('code_submitted', { surface: 'join' });

    publicApi
      .getCodePreview(code)
      .then((data) => {
        trackCaptureEvent('code_accepted', { surface: 'join' });
        setPreview(data);
        setStep('name');
      })
      .catch((err) => {
        const errCode = err?.code;
        if (errCode === 'INVALID_SHORT_CODE') {
          setError("That code isn't working. Ask whoever invited you for a fresh link.");
          setStep('error');
        } else if (errCode === 'INVITATION_EXPIRED' || errCode === 'INVITATION_CONSUMED') {
          setError('This invite has expired or has already been used. Ask whoever invited you for a fresh one.');
          setStep('error');
        } else {
          setStep('name');
        }
      });
  }, [code]);

  const handleNameSubmit = async (displayName: string) => {
    if (!code) return;

    try {
      const session = await guestApi.createSessionFromShortCode(code, displayName);
      trackCaptureEvent('session_created', {
        surface: 'join',
        entry: 'short_code',
        spaceId: session.spaceId,
        threadId: session.threadId ?? undefined,
      });
      if (session.csrfToken) {
        setCsrfToken(session.csrfToken);
      }

      setGuestSession({
        sessionToken: session.sessionToken,
        spaceId: session.spaceId,
        spaceName: session.spaceName,
        threadId: session.threadId ?? null,
        // Carry the server's scope through — the "My Stories" store keys its
        // dedupe on it, so dropping it makes two thread invites in one crew
        // clobber each other (W7).
        accessScope: session.accessScope,
        expiresAt: session.expiresAt,
        sessionId: session.sessionId,
        displayName,
      });

      if (session.threadId) {
        // A phase-invite short code carries a phaseIndex so the guest lands on the
        // invited phase; a plain thread code defaults to the latest incomplete phase.
        const query = session.phaseIndex != null ? `?phase=${session.phaseIndex}` : '';
        navigate(`/contribute/${session.threadId}${query}`);
      } else {
        setError("No moments here yet. The crew owner hasn't started one. Check back soon!");
        setStep('error');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't join with that code. Please try again.");
      setStep('error');
    }
  };

  if (step === 'loading') {
    return (
      <div class={joinStyles.page}>
        <ChorusHeader animate={false} />
        <div class={joinStyles.skeletonWrap}>
          <div class={styles.codeChip}>{code?.toUpperCase()}</div>
          <div style={{ height: '14px' }} />
          <div class={joinStyles.skeletonCard}>
            <Skeleton width="40%" height="12px" />
            <div style={{ height: '12px' }} />
            <Skeleton width="85%" height="24px" />
            <div style={{ height: '12px' }} />
            <Skeleton width="55%" height="14px" />
          </div>
          <div class={joinStyles.skeletonAfter}>
            <Skeleton width="100%" height="52px" borderRadius="var(--st-radius-md)" />
            <div style={{ height: '12px' }} />
            <Skeleton width="100%" height="52px" borderRadius="var(--st-radius-md)" />
          </div>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div class={joinStyles.page}>
        <ChorusHeader animate={false} />
        <div class={joinStyles.errorContainer}>
          <div class={joinStyles.errorEmpty}>
            <ChorusEmpty size={120} variant="dispersed" />
          </div>
          <h2 class={joinStyles.errorTitle}>Unable to Join</h2>
          <p class={joinStyles.errorMessage}>{error || 'Something went wrong'}</p>
          <a href="/" class={joinStyles.errorLink}>
            Go to OurTake home <IconArrow />
          </a>
        </div>
      </div>
    );
  }

  const threadType = preview?.threadType ?? null;
  const typeLabel = threadType ? THREAD_TYPE_LABELS[threadType] ?? threadType : null;
  const typeDesc = threadType ? THREAD_TYPE_DESCRIPTIONS[threadType] : null;

  return (
    <div class={joinStyles.page}>
      <ChorusHeader />

      <div class={joinStyles.intro}>
        {code && <div class={styles.codeChip}>Code: {code.toUpperCase()}</div>}

        {(preview?.senderDisplayName || preview?.creatorName) && (
          <p class={joinStyles.inviteText}>
            <span class={joinStyles.inviteName}>{preview.senderDisplayName || preview.creatorName}</span>
            {preview.phaseName
              ? preview.threadTitle
                ? <> invited you to share your take on <em>{preview.threadTitle}</em>.</>
                : ' invited you to share your take.'
              : ' invited you to join.'}
          </p>
        )}

        {preview && (
          <div class={joinStyles.previewCard}>
            <div class={joinStyles.heroRegion}>
              {preview.coverImageUrl ? (
                <img
                  src={preview.coverImageUrl}
                  alt={
                    preview.threadTitle
                      ? `Cover image for ${preview.threadTitle}`
                      : `Cover image for ${preview.spaceName}`
                  }
                  class={joinStyles.heroImage}
                  loading="eager"
                />
              ) : (
                <div class={joinStyles.heroFallback} aria-hidden="true">
                  <ChorusHero compact animate={false} labels={inviteIllustrationLabels(preview.invitationIllustrationNames)} />
                </div>
              )}
            </div>

            {preview.templateDisplayName && (
              <div class={joinStyles.eyebrow}>{preview.templateDisplayName}</div>
            )}

            <div class={joinStyles.previewMeta}>
              <AvatarStack
                size={22}
                overlap={7}
                colors={[
                  authorColor(preview.spaceName + 'a'),
                  authorColor(preview.spaceName + 'b'),
                  authorColor(preview.spaceName + 'c'),
                ]}
              />
              <span>
                {preview.spaceName} · {preview.memberCount} {preview.memberCount === 1 ? 'member' : 'members'}
                {preview.threadCount > 0 && (
                  <> · {preview.threadCount} {preview.threadCount === 1 ? 'moment' : 'moments'}</>
                )}
              </span>
            </div>

            {preview.threadTitle && (
              <>
                <div class={joinStyles.threadTitle}>{preview.threadTitle}</div>
                <div class={joinStyles.threadMeta}>
                  {typeLabel && <span class={joinStyles.typeBadge}>{typeLabel}</span>}
                  {preview.creatorName && (
                    <span class={joinStyles.threadStarted}>Started by {preview.creatorName}</span>
                  )}
                </div>
                {preview.threadDescription && (
                  <p class={joinStyles.threadDescription}>{preview.threadDescription}</p>
                )}
                {preview.plotSummary && (
                  <div class={joinStyles.plotSection}>
                    <div class={joinStyles.plotHeader}>The Plot</div>
                    <p class={joinStyles.plotText}>{preview.plotSummary}</p>
                  </div>
                )}
                {typeDesc && typeLabel && (
                  <div class={joinStyles.typeExplanation}>
                    <span class={joinStyles.typeExplanationStrong}>{typeLabel.toLowerCase()}</span> — {typeDesc}
                  </div>
                )}
              </>
            )}

            {/* Phase-invite short codes resolve a phase-scoped preview. */}
            {preview.phasePrompt && (
              <div class={joinStyles.promptPullquote} role="note">
                {preview.phasePrompt}
              </div>
            )}
            {preview.phaseName && (
              <div class={joinStyles.phaseInfo}>
                <span class={joinStyles.phaseArrow}>▸</span>
                Phase active: {preview.phaseName}
              </div>
            )}
          </div>
        )}
      </div>

      <div class={joinStyles.aboutWrap}>
        <button
          type="button"
          class={joinStyles.aboutToggle}
          onClick={() => setAboutExpanded(!aboutExpanded)}
          aria-expanded={aboutExpanded}
        >
          <span class={joinStyles.aboutToggleLeft}>What is OurTake?</span>
          <span
            class={`${joinStyles.aboutToggleSym} ${aboutExpanded ? joinStyles.aboutToggleSymOpen : ''}`}
            aria-hidden="true"
          >
            <IconPlus size={14} />
          </span>
        </button>
        {aboutExpanded && (
          <div class={joinStyles.aboutContent}>
            <p>
              When you share an experience, everyone sees it differently. OurTake is the only place
              where all those versions come together into the full story — not just yours.
            </p>
            <p>Your voice joins the others. There are no wrong answers.</p>
          </div>
        )}
      </div>

      <div class={joinStyles.nameWrap}>
        <h2 class={joinStyles.nameTitle}>What should we call you?</h2>
        <p class={joinStyles.nameHint}>
          This is how others will see you in the moment. First name is fine.
        </p>
        <DisplayNameForm onSubmit={handleNameSubmit} />
      </div>
    </div>
  );
}
